import React, { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  useBoolean,
  AlertDialogCloseButton,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useCompleteConversation } from '../../api/completeConversation';
import useStartCompanyScanFlow from '../../../../../../../company-scan/hooks/useStartCompanyScanFlow';
import useStartRiskAnalysisFlow from '../../../../../../../risk-analysis/src/hooks/useStartRiskAnalysisFlow';
import { useCreateRecommendation } from '../../api/createRecommendations';
import { useQueryClient } from 'react-query';
import { getSessionsByConversationIdQKey } from '../../api/getSessionsByConversationId';
import FLOW_TYPE from '../../../../../../../flows/src/constants/flowTypes';
import useFlowMeta from '../../../../../../../common/src/hooks/useFlowMeta';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  flowId: string;
  onSubmit: (recommendaitonId: string) => void;
}

const CreateAdvisoryReportWarningDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  conversationId,
  flowId,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useBoolean();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const startRiskAnalysis = useStartRiskAnalysisFlow();
  const startCompanyScan = useStartCompanyScanFlow();
  const { mutateAsync: completeConversation } =
    useCompleteConversation(conversationId);
  const { type } = useFlowMeta(flowId);

  const handleContinueConversation = () => {
    if (type === FLOW_TYPE.COMPANY_SCAN) {
      startCompanyScan(conversationId, undefined, undefined, flowId);
    } else startRiskAnalysis(conversationId);
  };
  const { mutateAsync: createRecommendation } = useCreateRecommendation();

  const handleCloseConversation = async () => {
    setIsLoading.on();
    const resp = await createRecommendation(conversationId);
    await completeConversation(resp.id);
    queryClient.invalidateQueries(
      getSessionsByConversationIdQKey(conversationId),
    );
    onSubmit(resp.id);
    onClose();
    setIsLoading.off();
  };

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {t('ADVISORY-REPORT_CREATE_WARNING-DIALOG_TITLE')}
            <AlertDialogCloseButton />
          </AlertDialogHeader>

          <AlertDialogBody>
            {t('ADVISORY-REPORT_CREATE_WARNING-DIALOG_DESCRIPTION')}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={handleContinueConversation}
              variant="secondary"
            >
              {t('CONVERSATIONS_DETAIL_HEADER_CONTINUE-CONVERSATION_TITLE')}
            </Button>
            <Button
              isLoading={isLoading}
              colorScheme="red"
              onClick={handleCloseConversation}
              ml={3}
            >
              {t('CLOSE-CONVERSATION')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default CreateAdvisoryReportWarningDialog;
